import React, {useState} from 'react';
import {formatDate} from '../../utils/dateUtils';
import '../card/eventCard.css';
import {EventSummaryInternalDto} from '../../common';
import {RecurrenceInfo} from './RecurrenceInfo';

type EventSummaryCardProps = React.FC<{
  event: EventSummaryInternalDto;
  onDeleteEvent: (eventId: string) => void;
  onClick: (eventId: string) => void;
  onEdit: (eventId: string) => void;
}>;

export const EventSummaryCard: EventSummaryCardProps = ({
  event,
  onDeleteEvent,
  onClick,
  onEdit,
}) => {
  const [hoveredButton, setHoveredButton] = useState<'edit' | 'delete' | null>(
    null,
  );

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete?')) {
      onDeleteEvent(event.id);
    }
  };

  const actionBarStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginBottom: '20px',
  };

  const buttonBaseStyle: React.CSSProperties = {
    padding: '10px 20px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '1em',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  };

  const editStyle: React.CSSProperties = {
    ...buttonBaseStyle,
    backgroundColor: hoveredButton === 'edit' ? '#cbd5e1' : '#e2e8f0', // Darkens on hover
    color: '#334155',
    transform: hoveredButton === 'edit' ? 'scale(1.05)' : 'scale(1)', // Slight grow effect
  };

  const deleteStyle: React.CSSProperties = {
    ...buttonBaseStyle,
    backgroundColor: hoveredButton === 'delete' ? '#fecaca' : '#fee2e2', // Redder on hover
    color: '#991b1b',
    transform: hoveredButton === 'delete' ? 'scale(1.05)' : 'scale(1)',
  };

  return (
    <div
      onClick={() => onClick(event?.id || '')}
      style={{cursor: 'pointer'}}
      className="event-card-container">
      <div style={actionBarStyle}>
        <button
          onMouseEnter={() => setHoveredButton('edit')}
          onMouseLeave={() => setHoveredButton(null)}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            onEdit(event.id || '');
          }}
          style={editStyle}>
          Edit
        </button>
        <button
          onMouseEnter={() => setHoveredButton('delete')}
          onMouseLeave={() => setHoveredButton(null)}
          onClick={handleDelete}
          style={deleteStyle}>
          Delete
        </button>
      </div>

      <div className="card-details">
        {/* An event without a photo showed a broken image. */}
        {event.photoUrls?.[0] && (
          <div className="card-image-wrapper">
            <img src={event.photoUrls[0]} alt="Event" className="card-image" />
          </div>
        )}
        <div className="card-text">
          <h2>
            <span className="label">Title:</span> {event.name}
          </h2>
          {event.occurrences?.length > 0 && (
            <>
              <p>
                <span className="label">Date Start:</span>{' '}
                {formatDate(event.occurrences[0]?.dateTimeStart)}
              </p>
              <p>
                <span className="label">Date End:</span>{' '}
                {formatDate(event.occurrences[0]?.dateTimeStop)}
              </p>
            </>
          )}
          {event.address?.placeName && (
            <p>
              <span className="label">Place Name: </span>{' '}
              {event.address.placeName}
            </p>
          )}
          <span className="label">Country: </span> {event.address?.country}
          <p>
            <span className="label">City: </span> {event.address?.cityName}
          </p>
          <p>
            <span className="label">Street: </span>{' '}
            {[event.address?.streetName, event.address?.streetNumber]
              .filter(Boolean)
              .join(' ') || '—'}
          </p>
          {event.groupId && (
            <RecurrenceInfo occurrences={event.occurrences} />
          )}
        </div>
      </div>
    </div>
  );
};
